// One record as a FEED CARD — the shape a transaction takes when it appears
// inside something else: a step in a process trail, a row behind a diagram
// node.
//
// The layout is the object's own, authored in the transaction editor's Feed
// view with the Feeds visual's drop wells (First/Second/Third Row × Left/Right)
// and resolved server-side. A well is named for a ROW, so a row is what gets
// drawn: Third row right sits beside Third row left, whether or not the two
// rows above it were filled in. Values arrive RAW with their column beside them, so
// formatting goes through the SAME <DisplayCell> every list cell uses — a date
// or an amount reads identically here and on the object's own screen.
//
// The header is what a trail loads; the body (narrative + the child form's
// detail rows) is a second query per card and most cards are never opened, so
// it loads on expand.
//
// One thing is drawn ON the card rather than inside its kebab: whether this
// record can be EDITED. A feed card's whole face opens the record, but nothing
// said so — the pencil (or the eye, when the row's own conditions make it
// read-only) was three-dots away, so a viewer reading a process rail had no way
// to tell an editable record from one they may only look at. It is the action's
// OWN icon, promoted out of the menu it already sits in, so it carries the same
// gating and adds one 15px glyph rather than a second control.

import { ChevronDown, ChevronRight, FileText, Lock } from 'lucide-react'

import type { ColumnDef } from '../types'
import { DisplayCell, displayPlainText, hasDisplayFormat } from '../displayFormat'
import { columnFormKey, ddlbDisplayLabel, richCellText, listHeadingOf } from '../utils'
import { personLabel, usePeople } from '../people'
import RowActionsMenu, { type RowAction } from '../list/RowActionsMenu'
import type { FeedBody, FeedCardModel, FeedCardRows, FeedCell } from './contract'

/** Plain text for a cell — the shared display format first, then the column's
 *  own ddlb/radio label (the stored value is a code; people read the label). */
export function cellText(cell: FeedCell): string {
  const head = cell.label ? `${cell.label} ` : ''
  if (cell.text != null) return head + cell.text
  const { value, col } = cell
  if (!col) return value == null ? '' : String(value)
  if (hasDisplayFormat(col)) return head + displayPlainText(value, col)
  const rich = richCellText(value, col)
  if (rich != null) return head + rich
  const label = ddlbDisplayLabel(value, col)
  if (label != null) return head + label
  if (value == null || value === '') return head.trim()
  if (typeof value === 'boolean') return head + (value ? 'Yes' : 'No')
  return head + String(value)
}

/** The authored layout, drawn. Exported because the compact record list draws
 *  the SAME feed definition — one form's feed view means one thing wherever it
 *  is shown, and a second implementation is a second thing to keep in step.
 *  `scale` nudges the type up for surfaces with more room than a trail rail.
 *
 *  Five tiers, so a card is read rather than scanned word by word:
 *    1. the HEADLINE — the first actual value on the card, the thing this
 *       record IS. The only bold on it.
 *    2. the amount on the second row right, which is what the eye goes to next.
 *    3. body text, plain.
 *    4. the third row, small and muted: metadata, not content.
 *    5. captions and headings — words the author added around the data, so they
 *       stay lighter and smaller than the value they belong to. A caption is
 *       never the bold thing; it is a label FOR the bold thing. */
export function FeedRows({ rows, scale = 1 }: { rows: FeedCardRows; scale?: number }) {
  const px = (n: number) => n * scale
  const lead = headlineCell(rows)
  return (
    <>
      <Line
        left={rows.first_left}
        right={rows.first_right}
        lead={lead}
        leftStyle={{ fontSize: px(13.5), fontWeight: 500, lineHeight: 1.35 }}
        rightStyle={{ fontSize: px(12), color: MUTED }}
      />
      <Line
        left={rows.second_left}
        right={rows.second_right}
        lead={lead}
        leftStyle={{ fontSize: px(12.5), lineHeight: 1.4 }}
        rightStyle={{ fontSize: px(12.5), fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
      />
      <Line
        left={rows.third_left}
        right={rows.third_right}
        lead={lead}
        leftStyle={{ fontSize: px(11.5), color: MUTED, marginTop: 1 }}
        rightStyle={{ fontSize: px(11.5), color: MUTED }}
      />
    </>
  )
}

/** The card's headline: the first cell down the left that holds a VALUE. A
 *  caption cannot be it — "Purchase Order" is what kind of record this is, the
 *  order number is which one. */
function headlineCell(rows: FeedCardRows): FeedCell | undefined {
  for (const cells of [rows.first_left, rows.second_left, rows.third_left]) {
    const hit = (cells || []).find((c) => c.text == null)
    if (hit) return hit
  }
  return undefined
}

/** What a record's save stamp READS as.
 *
 *  `cellText` hands back the column's display format when it has one and
 *  `String(value)` when it does not — and a DATETIME column that was never
 *  given a format arrives as `2026-07-24T15:49:30.980114`, which is the raw
 *  column and not a date anybody reads. The trail shows this line for records
 *  of six different objects, most of which never had a format authored, so an
 *  unformatted ISO timestamp is turned into one here. Anything else — a
 *  formatted value, a plain string — passes through untouched. */
export function stampText(cell: FeedCell): string {
  const raw = cellText(cell)
  const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/.exec(raw)
  if (!m) return raw
  const d = new Date(`${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}`)
  if (Number.isNaN(d.getTime())) return raw
  const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  return `${m[3]} ${MON[d.getMonth()]} ${m[1]}, ${m[4]}:${m[5]}`
}

const MUTED = 'var(--color-text-secondary, #64748b)'
const LOCK = 'var(--color-warning, #b45309)'

/** The value alone — the heading, when there is one, is drawn beside it. */
function CellValue({ cell }: { cell: FeedCell }) {
  // A user-key column reads as the person it points at, never as the key
  // (see `txn/people`). Ahead of the formats: the value is an identifier.
  const people = usePeople()
  if (cell.text != null) return <>{cell.text}</>
  const person = cell.col
    && personLabel(people, columnFormKey(cell.col), cell.value)
  if (person) return <>{person}</>
  if (cell.col && hasDisplayFormat(cell.col)) {
    return <DisplayCell value={cell.value} col={cell.col} />
  }
  return <>{cellText({ ...cell, label: undefined })}</>
}

/** Words the author put around the data — a caption, or a heading in front of
 *  a value. Lighter and a shade smaller than what they label, always. */
const ASIDE: React.CSSProperties = {
  fontWeight: 400, fontSize: '0.92em', color: MUTED, letterSpacing: '0.01em',
}

function Cell({ cell, lead }: { cell: FeedCell; lead?: FeedCell }) {
  // A caption is a label, never the loudest thing on the card.
  if (cell.text != null) return <span style={ASIDE}>{cell.text}</span>
  const value = (
    <span style={cell === lead ? { fontWeight: 700 } : undefined}>
      <CellValue cell={cell} />
    </span>
  )
  if (!cell.label) return value
  return (
    <>
      <span style={ASIDE}>{cell.label} </span>
      {value}
    </>
  )
}

/** Several columns in one well render as one line — the Feeds visual's own
 *  rule, so a well authored in either place means the same thing. */
function Well({ cells, lead }: { cells?: FeedCell[]; lead?: FeedCell }) {
  if (!cells || cells.length === 0) return null
  return (
    <>
      {cells.map((c, i) => (
        <span key={i}>
          {i > 0 ? '  ' : ''}
          <Cell cell={c} lead={lead} />
        </span>
      ))}
    </>
  )
}

function filled(cells?: FeedCell[]): boolean {
  return !!cells && cells.length > 0
}

/** ONE row of the card: the left well and the right well of the same row, on
 *  the same line. Stacking the three lefts and then the three rights would let
 *  a lone Third row right ride up beside the title — the well is named for a
 *  ROW, so the row is what has to be rendered.
 *
 *  Beside it WHERE THERE IS ROOM. A card in a trail rail can be 200px wide, and
 *  a right well holding a sentence ("Follow-up stopped after competitor
 *  contract signed.") used to hold its 45% and crush the left well into a
 *  column two words wide. The row wraps instead: both wells keep their own
 *  line, which is the readable answer at any width and changes nothing at all
 *  where the two already fit. */
function Line({ left, right, lead, leftStyle, rightStyle }: {
  left?: FeedCell[]
  right?: FeedCell[]
  /** The card's headline cell, so the one that is it renders strongest. */
  lead?: FeedCell
  leftStyle: React.CSSProperties
  rightStyle: React.CSSProperties
}) {
  if (!filled(left) && !filled(right)) return null
  return (
    <div style={{
      display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap',
    }}>
      <div style={{
        ...leftStyle, flex: '1 1 55%', minWidth: 0, overflowWrap: 'anywhere',
      }}>
        <Well cells={left} lead={lead} />
      </div>
      {filled(right) && (
        <div style={{
          ...rightStyle, flex: '0 1 auto', minWidth: 0, maxWidth: '100%',
          marginLeft: 'auto', textAlign: 'right', overflowWrap: 'anywhere',
        }}>
          <Well cells={right} lead={lead} />
        </div>
      )}
    </div>
  )
}

interface Props {
  card?: FeedCardModel
  /** Set when the viewer may not read this object: the step keeps its place
   *  and says only that records exist. */
  restricted?: boolean
  exists?: boolean
  /** Shown instead of the card when there is nothing to show. */
  title: string
  anchor?: boolean
  selected?: boolean
  body?: FeedBody | null
  bodyOpen?: boolean
  bodyBusy?: boolean
  onToggleBody?: () => void
  onOpen?: () => void
  /** Everything the viewer may do to this record — the SAME kebab the
   *  list rows carry, so a record offers the same actions wherever it is
   *  shown. */
  actions?: RowAction[]
  /** Stacking level for the actions menu — raised where the card is drawn
   *  inside a modal (see RowActionsMenu.MENU_Z_OVER_MODAL). */
  actionsZIndex?: number
  /** A line above the card's own rows: what KIND of record this is, when it
   *  happened, and any chips the host wants beside it (the anchor pill, a shut
   *  branch's count). A card laid out by its author may say none of those, and
   *  a trail has to show them for records of six different objects. Absent ⇒
   *  no kicker row, which is how every other caller draws it. */
  kicker?: { title: string; meta?: string; chips?: React.ReactNode }
  /** The 3px edge that says selected / anchor / neither. */
  barColor?: string
}

export default function FeedCard({
  card, restricted, exists, title, anchor, selected, body, bodyOpen, bodyBusy,
  onToggleBody, onOpen, actions, actionsZIndex, kicker, barColor,
}: Props) {
  if (restricted) {
    return (
      <div style={{
        border: '1px dashed ' + LOCK, borderRadius: 8, padding: '7px 10px',
        background: 'var(--color-bg-secondary, #fff)',
      }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: LOCK }}>{title}</div>
        <div style={{ fontSize: 13, color: LOCK, display: 'flex', alignItems: 'center', gap: 5 }}>
          <Lock size={12} />
          {exists
            ? "Records exist — you don't have access"
            : 'You don’t have access to this step'}
        </div>
      </div>
    )
  }

  if (!card) {
    return (
      <div style={{
        border: '1px dashed var(--color-border)', borderRadius: 8,
        padding: '7px 10px', opacity: 0.6,
      }}>
        <div style={{ fontSize: 14, fontWeight: 600 }}>{title}</div>
        <div style={{ fontSize: 13, color: MUTED }}>Nothing raised here yet</div>
      </div>
    )
  }

  const rows = card.rows || {}
  const canOpenBody = !!card.has_body && !!onToggleBody
  // Edit / View comes OUT of the kebab and onto the card — see the note at the
  // top of this file. Matched on `kind`, never on the label, because a designer
  // may rename the Edit system action and it is still the one that opens the
  // record. It leaves the menu rather than appearing in both places.
  const openAction = (actions || []).find((a) => a.kind === 'open')
  const menuActions = (actions || []).filter((a) => a !== openAction)

  return (
    <div style={{
      border: '1px solid ' + (selected ? 'var(--color-accent, #0061D0)' : 'var(--color-border)'),
      ...(barColor ? { borderLeft: '3px solid ' + barColor } : null),
      borderRadius: 8,
      background: selected
        ? 'var(--color-accent-weak, rgba(0,97,208,0.07))'
        : 'var(--color-bg, #fff)',
    }}>
      <div
        role={onOpen ? 'button' : undefined}
        tabIndex={onOpen ? 0 : undefined}
        onClick={onOpen}
        onKeyDown={(e) => {
          if (onOpen && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onOpen() }
        }}
        style={{
          display: 'flex', gap: 10, alignItems: 'flex-start', padding: '7px 10px',
          cursor: onOpen ? 'pointer' : undefined,
        }}
      >
        {canOpenBody ? (
          <button
            type="button"
            aria-expanded={!!bodyOpen}
            aria-label={(bodyOpen ? 'Hide' : 'Show') + ' details for ' + title}
            onClick={(e) => { e.stopPropagation(); onToggleBody!() }}
            style={{
              background: 'none', border: 0, padding: 0, cursor: 'pointer',
              color: MUTED, lineHeight: 1, marginTop: 2,
            }}
          >
            {bodyOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </button>
        ) : <span style={{ width: 14, flex: 'none' }} />}

        <div style={{ minWidth: 0, flex: 1 }}>
          {kicker && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2,
              flexWrap: 'wrap', rowGap: 3,
            }}>
              <span style={{
                fontSize: 13, fontWeight: 600, letterSpacing: '0.04em',
                textTransform: 'uppercase', color: MUTED,
                // The object's name is a label, not content: it gives up its
                // width before the chips and the stamp do, and it never wraps
                // onto a second line to do it.
                flex: '0 1 auto', minWidth: 0,
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}>{kicker.title}</span>
              {kicker.chips}
              <span style={{ flex: 1, minWidth: 0 }} />
              {kicker.meta && (
                <span style={{
                  fontSize: 13, color: MUTED, whiteSpace: 'nowrap',
                  flex: '0 1 auto', minWidth: 0, overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}>
                  {kicker.meta}
                </span>
              )}
            </div>
          )}
          <FeedRows rows={rows} />
          {anchor && !kicker && (
            <div style={{
              fontSize: 13, color: 'var(--color-accent, #0061D0)', fontWeight: 600,
              textAlign: 'right',
            }}>
              you are here
            </div>
          )}
        </div>

        {(openAction || menuActions.length > 0) && (
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              flex: 'none', marginTop: -2, display: 'flex', alignItems: 'center',
            }}
          >
            {openAction && <OpenButton action={openAction} />}
            <RowActionsMenu actions={menuActions} zIndex={actionsZIndex} />
          </div>
        )}
      </div>

      {bodyOpen && (
        <FeedBodyPanel body={body} busy={bodyBusy} style={{ margin: '0 10px 9px 34px' }} />
      )}
    </div>
  )
}

/** The one action a feed card wears on its face: open this record.
 *
 *  It draws the action's OWN icon and nothing else — a pencil when the row's
 *  conditions allow editing, an eye when they do not — so the card answers
 *  "can I change this?" at a glance without gaining a labelled button. Quiet
 *  until pointed at, the way the kebab beside it is. */
function OpenButton({ action }: { action: RowAction }) {
  return (
    <button
      type="button"
      className="btn btn-ghost btn-sm"
      title={action.label}
      aria-label={action.label}
      onClick={(e) => { e.stopPropagation(); action.onClick() }}
      style={{ padding: 4, color: MUTED, lineHeight: 1 }}
      onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--color-accent, #0061D0)' }}
      onMouseLeave={(e) => { e.currentTarget.style.color = MUTED }}
      onFocus={(e) => { e.currentTarget.style.color = 'var(--color-accent, #0061D0)' }}
      onBlur={(e) => { e.currentTarget.style.color = MUTED }}
    >
      {action.icon}
    </button>
  )
}

/** A card's body — the narrative and the child form's lines.
 *
 *  Exported because the trail draws a record's body in TWO places now: nested
 *  under its parent in the tree, and inside its step's box on the diagram. One
 *  panel, so an opened record reads the same either way. */
export function FeedBodyPanel({ body, busy, style }: {
  body?: FeedBody | null
  busy?: boolean
  style?: React.CSSProperties
}) {
  return (
    <div style={{
      padding: '8px 10px', borderRadius: 6,
      background: 'var(--color-bg-secondary, #f1f5f9)',
      border: '1px solid var(--color-border)',
      fontSize: 13.5, lineHeight: 1.5, color: 'var(--color-text)',
      ...style,
    }}>
      {busy && <span style={{ color: MUTED }}>Loading…</span>}
      {!busy && body && (
        <>
          {body.narrative.length > 0 && (
            <div>{body.narrative.map((c, i) => <Cell key={i} cell={c} />)}</div>
          )}
          {body.detail && body.detail.rows.length > 0 && (
            <DetailTable detail={body.detail} />
          )}
          {body.narrative.length === 0 && !body.detail && (
            <span style={{ color: MUTED }}>No further detail on this record.</span>
          )}
        </>
      )}
    </div>
  )
}

function DetailTable({ detail }: { detail: NonNullable<FeedBody['detail']> }) {
  const cellStyle: React.CSSProperties = {
    border: '1px solid var(--color-border)', padding: '3px 7px', textAlign: 'left',
  }
  return (
    <div style={{ overflowX: 'auto', marginTop: 7 }}>
      <table style={{ borderCollapse: 'collapse', fontSize: 13, background: 'var(--color-bg, #fff)' }}>
        <thead>
          <tr>
            {detail.columns.map((c: ColumnDef, i) => (
              <th key={i} style={{ ...cellStyle, fontWeight: 600, whiteSpace: 'nowrap' }}>
                {listHeadingOf(c)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {detail.rows.map((row, ri) => (
            <tr key={ri}>
              {row.map((cell, ci) => (
                <td key={ci} style={cellStyle}><Cell cell={cell} /></td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {detail.total > detail.shown && (
        <div style={{ fontSize: 13, color: MUTED, marginTop: 4 }}>
          Showing {detail.shown} of {detail.total} lines.
        </div>
      )}
    </div>
  )
}

/** The rail glyph for a step. Feed views may name an icon; anything we don't
 *  recognise falls back to a document, which is what every one of these is. */
export function FeedIcon({ locked }: { name?: string; locked?: boolean }) {
  return locked ? <Lock size={13} /> : <FileText size={13} />
}
